<div class="flex flex-col h-full w-full overflow-hidden bg-gray-50 select-text relative">
  <!-- Left Edge Resize Handle -->
  <div
    class="resize-handle group"
    (pointerdown)="startResize($event)"
    (dblclick)="layoutService.resetWidth()"
    [matTooltip]="layoutService.isExpanded() ? 'Sleep om breedte aan te passen' : 'Sleep om breedte aan te passen'"
  >
    <div class="resize-line"></div>
  </div>

  <!-- Panel Header -->
  <div class="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 shrink-0">
    <div class="flex items-center gap-2.5 min-w-0">
      <div class="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white shadow-sm shrink-0">
        <mat-icon class="text-base size-4 leading-none">auto_awesome</mat-icon>
      </div>
      <div class="min-w-0">
        <div class="flex items-center gap-2">
          <h3 class="!m-0 !p-0 text-sm font-semibold text-gray-900 leading-tight truncate">
            AI Assistent
          </h3>
          @if (currentSession().status === 'closed') {
            <span class="inline-flex items-center gap-1 px-1.5 py-0.2 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200 shrink-0">
              <mat-icon class="text-[10px] size-2.5 leading-none">lock</mat-icon>
              Afgerond
            </span>
          }
        </div>
        <p class="text-[11px] text-gray-500 truncate max-w-[190px]">
          {{ currentSession().title }}
        </p>
      </div>
    </div>

    <!-- Header Actions -->
    <div class="flex items-center gap-0.5 shrink-0">
      <button
        type="button"
        mat-button
        (click)="toggleHistory()"
        [class.!bg-indigo-50]="isHistoryOpen()"
        [class.!text-indigo-700]="isHistoryOpen()"
        class="!h-7 !px-2 !text-[11px] !text-gray-700 !rounded-lg border border-gray-200"
        matTooltip="Geschiedenis bekijken"
      >
        <mat-icon class="text-xs size-3.5 leading-none mr-1">history</mat-icon>
        ({{ savedSessionsCount() }})
      </button>

      <button
        type="button"
        mat-button
        (click)="startNewSession()"
        class="!h-7 !px-2 !text-[11px] !bg-indigo-600 !text-white hover:!bg-indigo-700 !rounded-lg"
        matTooltip="Nieuwe chat starten"
      >
        <mat-icon class="text-xs size-3.5 leading-none mr-0.5">add</mat-icon>
        Nieuw
      </button>

      <button
        mat-icon-button
        type="button"
        (click)="layoutService.toggleExpanded()"
        [matTooltip]="layoutService.isExpanded() ? 'Paneel versmallen' : 'Paneel verbreden'"
        class="p-0! inline-flex! items-center! justify-center! size-7! rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
      >
        <mat-icon class="flex! items-center! justify-center! text-base! size-4! leading-none!">
          {{ layoutService.isExpanded() ? 'close_fullscreen' : 'open_in_full' }}
        </mat-icon>
      </button>

      <button
        mat-icon-button
        type="button"
        (click)="closeChat()"
        matTooltip="Sluiten"
        class="p-0! inline-flex! items-center! justify-center! size-7! rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
      >
        <mat-icon class="flex! items-center! justify-center! text-base! size-4! leading-none!">close</mat-icon>
      </button>
    </div>
  </div>

  <!-- Main Container (History Overlay + Chat Area) -->
  <div class="flex flex-1 overflow-hidden relative">
    <!-- History Drawer (Toggleable) -->
    @if (isHistoryOpen()) {
      <div class="absolute inset-0 bg-white z-20 flex flex-col shadow-md animate-in fade-in duration-150">
        <div class="p-3 border-b border-gray-200 bg-gray-50/80 shrink-0">
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Eerdere sessies
            </span>
            <button
              mat-icon-button
              type="button"
              (click)="toggleHistory()"
              class="p-0! inline-flex! items-center! justify-center! size-6! text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-200/60 transition-colors"
              matTooltip="Sluit geschiedenis"
            >
              <mat-icon class="flex! items-center! justify-center! text-sm! size-3.5! leading-none!">close</mat-icon>
            </button>
          </div>

          <mat-form-field
            subscriptSizing="dynamic"
            class="w-full [--mat-form-field-container-text-size:12px] [--mat-form-field-container-height:34px] [--mat-form-field-container-vertical-padding:4px]"
          >
            <input
              matInput
              placeholder="Zoeken in geschiedenis..."
              [ngModel]="historySearch()"
              (ngModelChange)="historySearch.set($event)"
              autocomplete="off"
            />
          </mat-form-field>
        </div>

        <div class="flex-1 overflow-y-auto p-2 space-y-1">
          @for (session of filteredSessions(); track session.id) {
            <button
              type="button"
              (click)="selectSession(session.id); toggleHistory()"
              class="w-full text-left p-2.5 rounded-lg text-xs transition-colors flex flex-col gap-1 border"
              [class.bg-indigo-50]="session.id === selectedSessionId()"
              [class.border-indigo-200]="session.id === selectedSessionId()"
              [class.text-indigo-950]="session.id === selectedSessionId()"
              [class.bg-white]="session.id !== selectedSessionId()"
              [class.border-transparent]="session.id !== selectedSessionId()"
              [class.hover:bg-gray-100]="session.id !== selectedSessionId()"
            >
              <div class="flex items-center justify-between gap-1 w-full">
                <span class="font-medium truncate flex-1">{{ session.title }}</span>
                @if (session.status === 'closed') {
                  <span class="px-1.5 py-0.2 rounded text-[10px] font-medium bg-gray-100 text-gray-600 shrink-0">
                    Afgerond
                  </span>
                }
              </div>

              <div class="flex items-center justify-between text-[11px] text-gray-400">
                <span>{{ session.messages.length }} berichten</span>
                <span>{{ session.updatedAt | date: 'dd MMM HH:mm' }}</span>
              </div>
            </button>
          } @empty {
            <div class="p-4 text-center text-xs text-gray-400">
              Geen sessies gevonden
            </div>
          }
        </div>
      </div>
    }

    <!-- Chat Area -->
    <div class="flex-1 flex flex-col overflow-hidden bg-gray-50">
      <!-- Closed Session Notification Banner -->
      @if (isCurrentSessionClosed()) {
        <div class="px-3.5 py-2 bg-amber-50/90 border-b border-amber-200/80 text-amber-900 text-xs flex items-center justify-between shrink-0">
          <div class="flex items-center gap-1.5 text-[11px]">
            <mat-icon class="text-amber-600 text-sm size-3.5 leading-none">lock</mat-icon>
            <span>
              <strong>Sessie afgerond:</strong> Interactie is aangemaakt.
            </span>
          </div>
          <button
            type="button"
            mat-button
            class="!h-5.5 !px-2 !text-[11px] !bg-amber-100 hover:!bg-amber-200 !text-amber-900 !rounded shrink-0 font-medium"
            (click)="startNewSession()"
          >
            Nieuwe chat
          </button>
        </div>
      }

      <!-- Messages Scroll Area -->
      <div class="p-4 flex-1 overflow-y-auto space-y-4" #messagesContainer>
        @if (currentSession().messages.length === 0 && !isGenerating()) {
          <div class="h-full flex flex-col items-center justify-center text-center p-6 text-gray-400 select-none">
            <div class="w-11 h-11 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center mb-3">
              <mat-icon class="text-2xl size-6 leading-none">auto_awesome</mat-icon>
            </div>
            <p class="text-sm font-medium text-gray-700 mb-1">Nieuwe interactie ontwerpen</p>
            <p class="text-xs text-gray-400 max-w-[280px]">
              Typ hieronder wat voor interactie je wilt maken voor de robot.
            </p>
          </div>
        }

        @for (msg of currentSession().messages; track msg.id) {
          @if (msg.sender === 'status') {
            @if (msg.severity === 'error') {
              <!-- Failure notice: a full-width row rather than a pill, so the
                   message and the retry button each stay on one line. -->
              <div class="flex justify-center my-2 animate-in fade-in duration-150">
                <div
                  class="flex items-center gap-2.5 w-full max-w-[94%] px-3.5 py-2.5 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 text-xs shadow-2xs"
                >
                  <mat-icon class="text-sm size-4 leading-none text-amber-600 shrink-0">{{
                    msg.icon || 'error_outline'
                  }}</mat-icon>
                  <span class="font-medium flex-1 min-w-0">{{ msg.text }}</span>
                  @if ((msg.retryCount ?? 0) > 0) {
                    <span class="text-[11px] font-mono text-amber-500 shrink-0 whitespace-nowrap"
                      >poging {{ (msg.retryCount ?? 0) + 1 }}</span
                    >
                  }
                  <span class="text-[11px] font-mono text-amber-500 shrink-0 whitespace-nowrap">{{
                    msg.timestamp | date: 'HH:mm'
                  }}</span>
                  @if (msg.retryable) {
                    <button
                      type="button"
                      (click)="retryFailedTurn()"
                      [disabled]="isGenerating()"
                      class="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-700 disabled:bg-gray-300 disabled:text-gray-500 transition-colors"
                    >
                      <mat-icon class="text-sm size-3.5 leading-none">refresh</mat-icon>
                      Opnieuw proberen
                    </button>
                  }
                </div>
              </div>
            } @else {
              <div class="flex items-center justify-center my-2 animate-in fade-in duration-150">
                <div class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-50/90 border border-indigo-100/90 text-xs text-indigo-900 shadow-2xs select-none">
                  <mat-icon class="text-sm size-3.5 leading-none text-indigo-600 shrink-0">{{ msg.icon || 'smart_toy' }}</mat-icon>
                  <span class="font-medium">{{ msg.text }}</span>
                  <span class="text-[11px] text-indigo-400 font-mono ml-1">• {{ msg.timestamp | date: 'HH:mm' }}</span>
                </div>
              </div>
            }
          } @else {
            <div class="flex flex-col" [class.items-end]="msg.sender === 'user'" [class.items-start]="msg.sender === 'agent'">
              <!-- Message bubble header (Sender + Timestamp) -->
              <div class="flex items-center gap-1.5 mb-1 px-1 text-xs text-gray-400">
                @if (msg.sender === 'agent') {
                  <span class="font-semibold text-indigo-700 flex items-center gap-1">
                    <mat-icon class="text-sm size-3.5 leading-none">smart_toy</mat-icon>
                    Interactie Agent
                  </span>
                } @else {
                  <span class="font-semibold text-gray-700 flex items-center gap-1">
                    <mat-icon class="text-sm size-3.5 leading-none">person</mat-icon>
                    Jij
                  </span>
                }
                <span>•</span>
                <span class="text-[11px]">{{ msg.timestamp | date: 'HH:mm' }}</span>
              </div>

              <!-- Bubble Container -->
              <div
                class="max-w-[94%] rounded-2xl p-4 shadow-2xs leading-relaxed text-sm"
                [class.bg-indigo-600]="msg.sender === 'user'"
                [class.text-white]="msg.sender === 'user'"
                [class.rounded-tr-xs]="msg.sender === 'user'"
                [class.bg-white]="msg.sender === 'agent'"
                [class.text-gray-900]="msg.sender === 'agent'"
                [class.border]="msg.sender === 'agent'"
                [class.border-gray-200]="msg.sender === 'agent'"
                [class.rounded-tl-xs]="msg.sender === 'agent'"
              >
                @if (msg.text) {
                  <div class="chat-markdown-content" [innerHTML]="msg.text | markdown"></div>
                }

                <!-- Interaction Update Notification Card & Create Button -->
                @if (msg.interactionPreview; as preview) {
                  <div
                    class="p-3 rounded-xl border flex flex-col gap-2.5 transition-all cursor-pointer select-none hover:bg-indigo-50/70 hover:border-indigo-200"
                    [class.mt-3]="!!msg.text"
                    [class.bg-indigo-100]="isPreviewActive(msg.id)"
                    [class.border-indigo-400]="isPreviewActive(msg.id)"
                    [class.ring-2]="isPreviewActive(msg.id)"
                    [class.ring-indigo-400]="isPreviewActive(msg.id)"
                    [class.shadow-xs]="isPreviewActive(msg.id)"
                    [class.bg-emerald-50]="isChosenPreview(msg.id) && !isPreviewActive(msg.id)"
                    [class.border-emerald-300]="isChosenPreview(msg.id) && !isPreviewActive(msg.id)"
                    [class.bg-indigo-50]="!isChosenPreview(msg.id) && !isPreviewActive(msg.id)"
                    [class.border-indigo-100]="!isChosenPreview(msg.id) && !isPreviewActive(msg.id)"
                    (click)="selectPreview(msg)"
                    matTooltip="Klik om deze versie in de flow te tonen"
                  >
                    <div class="flex items-center justify-between gap-2">
                      <div class="flex items-center gap-2.5 min-w-0">
                        <div
                          class="w-8 h-8 rounded-lg text-indigo-700 flex items-center justify-center shrink-0"
                          [class.bg-indigo-200]="isPreviewActive(msg.id)"
                          [class.bg-emerald-100]="isChosenPreview(msg.id) && !isPreviewActive(msg.id)"
                          [class.bg-indigo-100]="!isChosenPreview(msg.id) && !isPreviewActive(msg.id)"
                        >
                          <mat-icon class="text-sm size-4 leading-none">account_tree</mat-icon>
                        </div>
                        <div class="min-w-0">
                          <div class="flex items-center gap-1.5">
                            <span class="text-xs font-semibold text-indigo-950 block truncate">
                              @if (isChosenPreview(msg.id)) {
                                Interactie aangemaakt (gekozen)
                              } @else {
                                Interactie bijgewerkt in flow
                              }
                            </span>
                            @if (isPreviewActive(msg.id)) {
                              <span class="text-[10px] font-semibold bg-indigo-600 text-white px-2 py-0.5 rounded-full shrink-0">
                                Actief in flow
                              </span>
                            }
                          </div>
                          <span class="text-xs text-indigo-600 font-medium block truncate">
                            {{ preview.title }} ({{ preview.questions.length }} {{ preview.questions.length === 1 ? 'vraag' : 'vragen' }})
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- Action button to create interaction & close session -->
                    @if (!isCurrentSessionClosed()) {
                      <div class="pt-2 border-t border-indigo-100/80 flex justify-end">
                        <button
                          type="button"
                          mat-button
                          class="!h-7.5 !px-3.5 !text-xs !bg-emerald-600 !text-white hover:!bg-emerald-700 !rounded-lg shadow-xs disabled:!bg-gray-300 disabled:!text-gray-500 flex items-center gap-1.5 font-semibold"
                          [disabled]="isCreatingInteraction()"
                          (click)="$event.stopPropagation(); createInteractionFromPreview(currentSession(), preview, msg.id)"
                        >
                          @if (isCreatingInteraction()) {
                            <mat-progress-spinner
                              mode="indeterminate"
                              diameter="14"
                              class="inline-block mr-1"
                            ></mat-progress-spinner>
                            <span>Bezig met aanmaken...</span>
                          } @else {
                            <span class="inline-flex items-center gap-1">
                              <mat-icon class="text-sm size-3.5 leading-none">check_circle</mat-icon>
                              <span>Interactie aanmaken</span>
                            </span>
                          }
                        </button>
                      </div>
                    } @else {
                      <div class="pt-2 border-t border-indigo-100/80 flex items-center justify-between text-xs">
                        @if (isChosenPreview(msg.id)) {
                          <span class="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                            <mat-icon class="text-sm size-3.5 leading-none text-emerald-600">check_circle</mat-icon>
                            Gekozen versie
                          </span>
                          <span class="text-emerald-700 text-[11px] font-semibold bg-emerald-100 px-2 py-0.5 rounded">
                            Aangemaakt
                          </span>
                        } @else {
                          <span class="inline-flex items-center gap-1 text-gray-500 font-normal">
                            <mat-icon class="text-sm size-3.5 leading-none text-gray-400">history</mat-icon>
                            Eerdere tussenversie
                          </span>
                          <span class="text-gray-400 text-[11px]">
                            Niet gekozen
                          </span>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          }
        }

        <!-- Generating / Activity Live Indicator -->
        @if (isGenerating()) {
          <div class="flex flex-col items-start animate-in fade-in duration-200">
            <div class="flex items-center gap-1 mb-1 px-1 text-xs text-indigo-700 font-semibold">
              <mat-icon class="text-sm size-3.5 leading-none">smart_toy</mat-icon>
              Interactie Agent
            </div>
            <div class="bg-white border border-indigo-200/90 rounded-2xl rounded-tl-xs p-3.5 shadow-xs flex items-center gap-3">
              <div class="relative flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 shrink-0">
                <mat-icon class="text-base size-4 leading-none animate-pulse">
                  {{ currentActivity()?.icon || 'psychology' }}
                </mat-icon>
                <span class="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                  <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                  <span class="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-500"></span>
                </span>
              </div>
              <div class="flex flex-col min-w-0">
                <span class="text-xs font-semibold text-gray-800 truncate">
                  {{ currentActivity()?.message || 'Bezig met genereren...' }}
                </span>
                <span class="text-[11px] text-gray-500">
                  Even geduld alstublieft
                </span>
              </div>
            </div>
          </div>
        }
      </div>

      <!-- Input Footer -->
      <div class="p-3.5 bg-white border-t border-gray-200 shrink-0">
        @if (isCurrentSessionClosed()) {
          <div class="flex items-center justify-between p-2.5 bg-gray-100 rounded-xl text-xs text-gray-600">
            <div class="flex items-center gap-1.5">
              <mat-icon class="text-gray-500 text-sm size-3.5 leading-none">lock</mat-icon>
              <span>Deze sessie is afgerond.</span>
            </div>
            <button
              type="button"
              mat-button
              (click)="startNewSession()"
              class="!h-6.5 !px-3 !text-xs !bg-indigo-600 !text-white hover:!bg-indigo-700 !rounded-lg"
            >
              <mat-icon class="text-xs size-3 leading-none mr-0.5">add</mat-icon>
              Nieuwe chat
            </button>
          </div>
        } @else {
          <form (ngSubmit)="sendMessage()" class="flex items-end gap-2">
            <mat-form-field
              subscriptSizing="dynamic"
              class="w-full [--mat-form-field-container-text-size:13.5px] [--mat-form-field-container-vertical-padding:6px]"
            >
              <textarea
                matInput
                cdkTextareaAutosize
                [cdkAutosizeMinRows]="1"
                [cdkAutosizeMaxRows]="6"
                placeholder="Typ een bericht of vraag..."
                [ngModel]="userInput()"
                (ngModelChange)="userInput.set($event)"
                name="chatInput"
                (keydown)="handleKeyDown($event)"
                [disabled]="isGenerating()"
                class="resize-none leading-relaxed py-1"
              ></textarea>

              @if (isGenerating()) {
                <mat-progress-spinner
                  matSuffix
                  mode="indeterminate"
                  diameter="18"
                  class="mr-1.5"
                ></mat-progress-spinner>
              }
            </mat-form-field>

            <button
              mat-icon-button
              type="submit"
              [disabled]="!userInput().trim() || isGenerating()"
              class="p-0! inline-flex! items-center! justify-center! !bg-indigo-600 !text-white hover:!bg-indigo-700 disabled:!bg-gray-200 disabled:!text-gray-400 shrink-0 size-9.5! !rounded-xl transition-colors mb-0.5"
              matTooltip="Versturen (Enter, Shift+Enter voor nieuwe regel)"
            >
              <mat-icon class="flex! items-center! justify-center! text-base! size-4! leading-none!">send</mat-icon>
            </button>
          </form>
        }
      </div>
    </div>
  </div>
</div>
